// © 2026 Buddy Media LLC. All rights reserved.
import { useEffect, useMemo, useState } from "react";

import { AdminCard } from "@/components/AdminCard";
import { AdminPageShell } from "@/components/AdminPageShell";
import { apiFetch, describeApiError } from "@/lib/api";

// T-082 Phase 5 admin surface — token-budget config dashboard.
//
// What it does:
//   * Two account cards (Max-1, Max-2) with editable label /
//     anthropic_account / plan_tier dropdown / per-period caps
//   * "Paste current" form per account: Marc enters input + output
//     tokens read from Claude.ai's usage indicator → backend stamps
//     baseline_ts + baseline counters; future events accumulate
//     from there.
//   * Actor-assignment matrix: each actor (frontend-worker, backend-worker, chat-opus,
//     shadow) has a radio-style picker over (Max-1, Max-2,
//     unassigned) + an enabled checkbox.
//   * Threshold table (read-only v1).
//   * Rolled-up consumption per account (baseline + delta from
//     reported events).
//
// Backend: /api/admin/token-budget/* — admin-only.
// MCP-resident data via the backend admin proxy (mirrors q-stream).

type BillingMode = "token_cap" | "credit_usd" | "manual_pct";

type PlanTier = {
  slug: string;
  label: string;
  billing_mode: BillingMode;
  weekly_input_token_cap: number | null;
  weekly_output_token_cap: number | null;
  period_input_token_cap: number | null;
  period_output_token_cap: number | null;
  input_hcents_per_mtok: number | null;
  output_hcents_per_mtok: number | null;
};

type Account = {
  id: number;
  label: string;
  anthropic_account: string | null;
  plan_tier: string;
  billing_mode: BillingMode;
  weekly_input_token_cap: number | null;
  weekly_output_token_cap: number | null;
  period_input_token_cap: number | null;
  period_output_token_cap: number | null;
  period_anchor: string;
  baseline_ts: string | null;
  baseline_input_tokens_used: number;
  baseline_output_tokens_used: number;
  consumed_since_baseline_input: number;
  consumed_since_baseline_output: number;
  current_input_tokens_used: number;
  current_output_tokens_used: number;
  // Phase 8 — credit fields (Claude API accounts)
  last_topup_amount_cents: number | null;
  last_topup_at: string | null;
  recharge_threshold_cents: number | null;
  recharge_to_cents: number | null;
  spent_hcents_since_topup: number | null;
  current_balance_cents: number | null;
  // T-088 — Max-plan readings (manual_pct accounts)
  session_window_hours: number | null;
  session_started_at: string | null;
  current_session_pct: number | null;
  current_weekly_all_pct: number | null;
  current_weekly_sonnet_pct: number | null;
  last_reading_at: string | null;
  session_resets_at: string | null;
  weekly_resets_at: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
};

type Actor = {
  actor_handle: string;
  account_id: number | null;
  account_label: string | null;
  account_plan_tier?: string | null;
  enabled: boolean;
  // Per-actor utilization (Marc 2026-05-09 redo). Optional for back-
  // compat with older MCP servers that don't return them yet.
  input_tokens_24h?: number;
  output_tokens_24h?: number;
  cost_hcents_24h?: number;
  input_tokens_week?: number;
  output_tokens_week?: number;
  cost_hcents_week?: number;
  input_tokens_since_baseline?: number;
  output_tokens_since_baseline?: number;
  event_count_total?: number;
  last_event_ts?: string | null;
};

type Threshold = {
  id: number;
  account_id: number | null;
  account_label: string | null;
  period_type: "week" | "period";
  alert_at_pct: number | null;
  project_alert_at_pct: number | null;
  cooldown_hours: number;
};

type Alert = {
  id: number;
  ts: string;
  account_id: number | null;
  account_label: string | null;
  period_type: "week" | "period";
  threshold_pct_at_alert: number | null;
  projected_pct_at_period_end: number | null;
  recommendation_text: string | null;
  email_message_id: string | null;
};

type State = {
  accounts: Account[];
  actors: Actor[];
  thresholds: Threshold[];
  plan_tiers: PlanTier[];
  recent_alerts?: Alert[];
};

const describeError = describeApiError;

function fmtNum(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("en-US");
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().replace("T", " ").slice(0, 19) + " UTC";
}

function pctOrDash(used: number, cap: number | null): string {
  if (cap === null || cap === 0) return "—";
  return `${Math.round((used / cap) * 100)}%`;
}

function fmtUSD(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtUSDFromHcents(hcents: number | null | undefined): string {
  if (hcents === null || hcents === undefined) return "—";
  // hcents = 100ths of a cent → divide by 10000 for dollars
  return `$${(hcents / 10000).toFixed(4)}`.replace(/\.?0+$/, "") || "$0";
}

function dollarsToCents(s: string): number | null {
  const trimmed = s.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

// T-088 — convert UTC ISO timestamps to local <input type="datetime-local">
// values and back. The browser-native datetime-local picker handles
// timezones invisibly; we always store in UTC ISO on the server side.
function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToISO(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// "in 2h 14m" / "in 3d 4h" countdown for reset timers.
function fmtCountdown(iso: string | null | undefined): string {
  if (!iso) return "—";
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return "—";
  const diffMs = target - Date.now();
  if (diffMs <= 0) return "due now";
  const totalMin = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const mins = totalMin % 60;
  if (days > 0) return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${mins}m`;
  return `in ${mins}m`;
}

function pctBarColor(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return "bg-slate-700";
  if (pct >= 90) return "bg-rose-500";
  if (pct >= 75) return "bg-amber-400";
  if (pct >= 50) return "bg-violet-500";
  return "bg-emerald-500";
}

// Auto-refresh cadence options (Marc 2026-05-09 redo). 0 = off.
const REFRESH_OPTIONS_S = [0, 15, 30, 60, 300] as const;
const REFRESH_STORAGE_KEY = "bw.admin.token-budget.refresh-s";

function loadRefreshSeconds(): number {
  if (typeof window === "undefined") return 30;
  const raw = window.localStorage.getItem(REFRESH_STORAGE_KEY);
  const n = raw === null ? 30 : Number(raw);
  return REFRESH_OPTIONS_S.includes(n as (typeof REFRESH_OPTIONS_S)[number])
    ? n
    : 30;
}

export function OpsTokenBudgetPage() {
  const [state, setState] = useState<State | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [saving, setSaving] = useState<string | null>(null);
  const [refreshSeconds, setRefreshSeconds] = useState<number>(
    loadRefreshSeconds,
  );
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Client-side timeout — apiFetch doesn't carry one, and the
    // BW backend's MCP proxy can hang up to 30s waiting on its
    // upstream. Without this the loading state never clears when
    // the BW→MCP path is degraded; user sees "Refreshing…" forever.
    const fetchTimeoutMs = 12_000;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      cancelled = true;
      setError(
        `Request timed out after ${fetchTimeoutMs / 1000}s. The backend is ` +
          `likely unable to reach the MCP server — check ` +
          `/admin/operations/cluster-topology or T-644.`,
      );
      setLoading(false);
    }, fetchTimeoutMs);
    apiFetch<State>("/api/admin/token-budget")
      .then((data) => {
        if (!cancelled) {
          setState(data);
          setLastFetchedAt(Date.now());
        }
      })
      .catch((err) => {
        if (!cancelled) setError(describeError(err, "Load failed"));
      })
      .finally(() => {
        if (!cancelled) {
          window.clearTimeout(timer);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [refreshKey]);

  // Auto-refresh timer. 0 = off. Persists to localStorage so the
  // setting survives page reloads.
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(REFRESH_STORAGE_KEY, String(refreshSeconds));
    }
    if (refreshSeconds <= 0) return undefined;
    const handle = window.setInterval(() => {
      setRefreshKey((k) => k + 1);
    }, refreshSeconds * 1000);
    return () => window.clearInterval(handle);
  }, [refreshSeconds]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const refreshAfter = async <T,>(p: Promise<T>): Promise<T> => {
    try {
      return await p;
    } finally {
      refresh();
    }
  };

  const updateAccount = async (id: number, patch: Partial<Account>) => {
    setSaving(`account-${id}`);
    try {
      await refreshAfter(
        apiFetch(`/api/admin/token-budget/accounts/${id}`, {
          method: "PATCH",
          body: JSON.stringify(patch),
        }),
      );
    } catch (err) {
      setError(describeError(err, "Update failed"));
    } finally {
      setSaving(null);
    }
  };

  const setBaseline = async (
    id: number,
    input_tokens_used: number,
    output_tokens_used: number,
  ) => {
    setSaving(`baseline-${id}`);
    try {
      await refreshAfter(
        apiFetch(`/api/admin/token-budget/accounts/${id}/baseline`, {
          method: "POST",
          body: JSON.stringify({ input_tokens_used, output_tokens_used }),
        }),
      );
    } catch (err) {
      setError(describeError(err, "Baseline paste failed"));
    } finally {
      setSaving(null);
    }
  };

  // T-088 — Max-plan readings. Marc reads pcts + reset timestamps off
  // claude.ai's usage UI (Anthropic's weighting is opaque) and pushes
  // them here. Empty fields are not sent — only the keys that changed.
  const setMaxPlanReading = async (
    id: number,
    body: Record<string, unknown>,
  ) => {
    setSaving(`max-reading-${id}`);
    try {
      await refreshAfter(
        apiFetch(`/api/admin/token-budget/accounts/${id}/max-plan-reading`, {
          method: "POST",
          body: JSON.stringify(body),
        }),
      );
    } catch (err) {
      setError(describeError(err, "Reading update failed"));
    } finally {
      setSaving(null);
    }
  };

  const assignActor = async (
    actor_handle: string,
    account_id: number | null,
    enabled?: boolean,
  ) => {
    setSaving(`actor-${actor_handle}`);
    try {
      const body: Record<string, unknown> = { account_id };
      if (enabled !== undefined) body.enabled = enabled;
      await refreshAfter(
        apiFetch(
          `/api/admin/token-budget/actors/${encodeURIComponent(actor_handle)}`,
          {
            method: "POST",
            body: JSON.stringify(body),
          },
        ),
      );
    } catch (err) {
      setError(describeError(err, "Assign failed"));
    } finally {
      setSaving(null);
    }
  };

  const runProjectionTick = async () => {
    setSaving("projection");
    try {
      const result = await apiFetch<{
        ok: boolean;
        accounts_evaluated: number;
        alerts_fired: { account_label: string; threshold_pct_at_alert: number | null }[];
      }>(`/api/admin/token-budget/projection-tick`, { method: "POST" });
      const fired = result.alerts_fired?.length ?? 0;
      const summary =
        fired === 0
          ? `Evaluated ${result.accounts_evaluated} accounts; no thresholds crossed.`
          : `Fired ${fired} alert${fired === 1 ? "" : "s"} across ${result.accounts_evaluated} accounts.`;
      alert(summary);
      refresh();
    } catch (err) {
      setError(describeError(err, "Projection tick failed"));
    } finally {
      setSaving(null);
    }
  };

  return (
    <AdminPageShell
      title="Token budget"
      description="Per-actor Anthropic-API spend across the cluster — usage, baselines, throttle state, projections."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-200">
            <span
              aria-hidden
              className="inline-block h-2 w-2 animate-pulse rounded-full"
              style={{
                background:
                  refreshSeconds > 0
                    ? "var(--brand-accent)"
                    : "var(--bw-muted, #64748b)",
                animationDuration: `${Math.max(refreshSeconds, 1.5)}s`,
              }}
              title={refreshSeconds > 0 ? `Polling every ${refreshSeconds}s` : "Auto-refresh paused"}
            />
            Auto-refresh
            <select
              value={refreshSeconds}
              onChange={(e) => setRefreshSeconds(Number(e.target.value))}
              className="rounded-md border bg-slate-900/80 px-3 py-1.5 text-sm text-white transition-colors focus:outline-none"
              style={{
                borderColor: "var(--jc-border)",
              }}
              title="Polling cadence for the dashboard. Persists to localStorage."
            >
              {REFRESH_OPTIONS_S.map((s) => (
                <option key={s} value={s}>
                  {s === 0 ? "off" : s < 60 ? `${s}s` : `${s / 60}m`}
                </option>
              ))}
            </select>
          </label>
          {lastFetchedAt && (
            <span
              className="text-xs text-slate-400"
              title={new Date(lastFetchedAt).toISOString()}
            >
              Last refreshed {Math.max(0, Math.round((Date.now() - lastFetchedAt) / 1000))}s ago
            </span>
          )}
          <button
            type="button"
            onClick={runProjectionTick}
            disabled={saving === "projection"}
            className="rounded-md px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-all hover:brightness-110 disabled:opacity-50"
            style={{
              background:
                "linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-accent) 100%)",
            }}
            title="Run the projection + threshold-evaluation pass now (cron also fires this every 30 min)"
          >
            {saving === "projection" ? "Projecting…" : "Run projection"}
          </button>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="rounded-md border bg-slate-900/60 px-3 py-1.5 text-sm font-medium text-slate-200 transition-colors hover:border-slate-500 disabled:opacity-50"
            style={{ borderColor: "var(--jc-border)" }}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {error && (
          <AdminCard className="border-rose-500/40 bg-rose-500/10">
            <div className="flex items-start gap-3">
              <span aria-hidden className="text-xl">⚠️</span>
              <div className="flex-1">
                <p className="text-sm font-medium text-rose-100">
                  Couldn't load the dashboard
                </p>
                <p className="mt-1 text-xs text-rose-200/80">{error}</p>
                <p className="mt-2 text-xs text-rose-200/70">
                  This usually means the BW backend can't reach the MCP
                  server. Token-budget data lives in MCP-resident
                  Postgres (T-082); the backend proxies through.
                  Cluster-mesh diagnostics: <code>/admin/operations/cluster-topology</code>.
                </p>
              </div>
            </div>
          </AdminCard>
        )}

        {!state && !loading && !error && (
          <AdminCard>
            <div className="flex items-center gap-3 py-2">
              <div
                aria-hidden
                className="h-8 w-8 animate-pulse rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, var(--brand-primary) 0%, var(--brand-accent) 100%)",
                }}
              />
              <p className="text-sm text-slate-300">
                Loading token-budget snapshot…
              </p>
            </div>
          </AdminCard>
        )}

        {state && (
          <>
            <div className="grid gap-4 lg:grid-cols-2">
              {state.accounts.map((acc) => (
                <AccountCard
                  key={acc.id}
                  account={acc}
                  planTiers={state.plan_tiers}
                  saving={saving}
                  onUpdate={(patch) => updateAccount(acc.id, patch)}
                  onBaseline={(i, o) => setBaseline(acc.id, i, o)}
                  onMaxPlanReading={(body) => setMaxPlanReading(acc.id, body)}
                />
              ))}
            </div>

            <ActorUtilization actors={state.actors} />

            <ActorAssignments
              actors={state.actors}
              accounts={state.accounts}
              saving={saving}
              onAssign={assignActor}
            />

            <ThresholdTable thresholds={state.thresholds} />

            <AlertsTable alerts={state.recent_alerts ?? []} />
          </>
        )}
      </div>
    </AdminPageShell>
  );
}

function AccountCard({
  account,
  planTiers,
  saving,
  onUpdate,
  onBaseline,
  onMaxPlanReading,
}: {
  account: Account;
  planTiers: PlanTier[];
  saving: string | null;
  onUpdate: (patch: Partial<Account>) => void;
  onBaseline: (input: number, output: number) => void;
  onMaxPlanReading: (body: Record<string, unknown>) => void;
}) {
  const [draftLabel, setDraftLabel] = useState(account.label);
  const [draftEmail, setDraftEmail] = useState(account.anthropic_account ?? "");
  const [draftPlan, setDraftPlan] = useState(account.plan_tier);
  const [draftWeeklyIn, setDraftWeeklyIn] = useState(
    account.weekly_input_token_cap?.toString() ?? "",
  );
  const [draftWeeklyOut, setDraftWeeklyOut] = useState(
    account.weekly_output_token_cap?.toString() ?? "",
  );

  const [draftBaselineIn, setDraftBaselineIn] = useState(
    account.baseline_input_tokens_used.toString(),
  );
  const [draftBaselineOut, setDraftBaselineOut] = useState(
    account.baseline_output_tokens_used.toString(),
  );

  // Phase 8 — credit-mode form state. Stored as user-typed dollar
  // strings; converted to cents on submit. Defaults pulled from the
  // account row so refresh reflects the saved values.
  const [draftTopupAmount, setDraftTopupAmount] = useState(
    account.last_topup_amount_cents !== null
      ? (account.last_topup_amount_cents / 100).toFixed(2)
      : "",
  );
  const [draftRechargeAt, setDraftRechargeAt] = useState(
    account.recharge_threshold_cents !== null
      ? (account.recharge_threshold_cents / 100).toFixed(2)
      : "",
  );
  const [draftRechargeTo, setDraftRechargeTo] = useState(
    account.recharge_to_cents !== null
      ? (account.recharge_to_cents / 100).toFixed(2)
      : "",
  );

  const isCreditMode = account.billing_mode === "credit_usd";
  const isManualPctMode = account.billing_mode === "manual_pct";

  // T-088 — Max-plan reading drafts. All optional; only changed
  // values get sent. The reset-at fields accept the local datetime
  // input and convert to ISO before submit.
  const [draftSessionPct, setDraftSessionPct] = useState(
    account.current_session_pct?.toString() ?? "",
  );
  const [draftWeeklyAllPct, setDraftWeeklyAllPct] = useState(
    account.current_weekly_all_pct?.toString() ?? "",
  );
  const [draftWeeklySonnetPct, setDraftWeeklySonnetPct] = useState(
    account.current_weekly_sonnet_pct?.toString() ?? "",
  );
  const [draftSessionResetsAt, setDraftSessionResetsAt] = useState(
    isoToLocalInput(account.session_resets_at),
  );
  const [draftWeeklyResetsAt, setDraftWeeklyResetsAt] = useState(
    isoToLocalInput(account.weekly_resets_at),
  );
  const [draftSessionWindowH, setDraftSessionWindowH] = useState(
    account.session_window_hours?.toString() ?? "",
  );

  const dirty = useMemo(() => {
    const baseDirty =
      draftLabel !== account.label ||
      draftEmail !== (account.anthropic_account ?? "") ||
      draftPlan !== account.plan_tier;
    if (isCreditMode) {
      return (
        baseDirty ||
        draftRechargeAt !==
          (account.recharge_threshold_cents !== null
            ? (account.recharge_threshold_cents / 100).toFixed(2)
            : "") ||
        draftRechargeTo !==
          (account.recharge_to_cents !== null
            ? (account.recharge_to_cents / 100).toFixed(2)
            : "")
      );
    }
    return (
      baseDirty ||
      draftWeeklyIn !== (account.weekly_input_token_cap?.toString() ?? "") ||
      draftWeeklyOut !== (account.weekly_output_token_cap?.toString() ?? "")
    );
  }, [
    draftLabel,
    draftEmail,
    draftPlan,
    draftWeeklyIn,
    draftWeeklyOut,
    draftRechargeAt,
    draftRechargeTo,
    isCreditMode,
    account,
  ]);

  const isAccountSaving = saving === `account-${account.id}`;
  const isBaselineSaving = saving === `baseline-${account.id}`;

  const submitConfig = () => {
    const patch: Partial<Account> = {};
    if (draftLabel !== account.label) patch.label = draftLabel;
    if (draftEmail !== (account.anthropic_account ?? "")) {
      patch.anthropic_account = draftEmail || null;
    }
    if (draftPlan !== account.plan_tier) patch.plan_tier = draftPlan;
    if (isCreditMode) {
      const rAt = dollarsToCents(draftRechargeAt);
      if (rAt !== account.recharge_threshold_cents)
        patch.recharge_threshold_cents = rAt;
      const rTo = dollarsToCents(draftRechargeTo);
      if (rTo !== account.recharge_to_cents) patch.recharge_to_cents = rTo;
    } else {
      const wIn =
        draftWeeklyIn.trim() === "" ? null : parseInt(draftWeeklyIn, 10);
      if (wIn !== account.weekly_input_token_cap)
        patch.weekly_input_token_cap = wIn;
      const wOut =
        draftWeeklyOut.trim() === "" ? null : parseInt(draftWeeklyOut, 10);
      if (wOut !== account.weekly_output_token_cap)
        patch.weekly_output_token_cap = wOut;
    }
    onUpdate(patch);
  };

  const submitTopup = () => {
    const cents = dollarsToCents(draftTopupAmount);
    if (cents === null || cents === 0) {
      alert("Enter a valid top-up amount in dollars (e.g., 100 or 50.00).");
      return;
    }
    if (
      !confirm(
        `Record top-up for ${account.label}?\n\n` +
          `  Amount: $${(cents / 100).toFixed(2)}\n` +
          `  Stamps last_topup_at to now; future events accumulate cost from this anchor.`,
      )
    )
      return;
    onUpdate({
      last_topup_amount_cents: cents,
      last_topup_at: new Date().toISOString(),
    });
  };

  const submitBaseline = () => {
    const i = parseInt(draftBaselineIn, 10) || 0;
    const o = parseInt(draftBaselineOut, 10) || 0;
    if (
      !confirm(
        `Set baseline for ${account.label}?\n\n` +
          `  input  = ${i.toLocaleString()}\n` +
          `  output = ${o.toLocaleString()}\n\n` +
          `Future events accumulate FROM these starting points; baseline_ts stamped to now.`,
      )
    )
      return;
    onBaseline(i, o);
  };

  // T-088 — push only changed manual_pct fields. Empty string clears
  // (sends null); unchanged fields are omitted entirely.
  const submitMaxPlanReading = (sessionJustRestarted: boolean) => {
    const body: Record<string, unknown> = {};
    if (sessionJustRestarted) {
      body.session_just_restarted = true;
    } else {
      const pushPct = (
        key: string,
        draft: string,
        original: number | null,
      ) => {
        const trimmed = draft.trim();
        if (trimmed === "" && original === null) return;
        const num = trimmed === "" ? null : parseInt(trimmed, 10);
        if (num !== original) body[key] = num;
      };
      pushPct(
        "session_pct",
        draftSessionPct,
        account.current_session_pct ?? null,
      );
      pushPct(
        "weekly_all_pct",
        draftWeeklyAllPct,
        account.current_weekly_all_pct ?? null,
      );
      pushPct(
        "weekly_sonnet_pct",
        draftWeeklySonnetPct,
        account.current_weekly_sonnet_pct ?? null,
      );
      const sIso = localInputToISO(draftSessionResetsAt);
      if (sIso !== account.session_resets_at) body.session_resets_at = sIso;
      const wIso = localInputToISO(draftWeeklyResetsAt);
      if (wIso !== account.weekly_resets_at) body.weekly_resets_at = wIso;
      const winH =
        draftSessionWindowH.trim() === ""
          ? null
          : parseInt(draftSessionWindowH, 10);
      if (winH !== (account.session_window_hours ?? null))
        body.session_window_hours = winH;
    }
    if (Object.keys(body).length === 0) return;
    onMaxPlanReading(body);
  };

  return (
    <AdminCard className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold text-white">
          <span className="text-white">{account.label}</span>
        </h2>
        <span className="text-xs font-medium text-slate-400">
          id #{account.id}
        </span>
      </div>

      {isManualPctMode && (
        <>
          <div className="rounded-md border border-slate-800 bg-slate-950/50 p-3">
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <div className="text-xs font-medium text-slate-400">
                Live readings · from claude.ai usage UI
              </div>
              <div className="text-xs text-slate-400">
                {account.last_reading_at
                  ? `read ${fmtTime(account.last_reading_at)}`
                  : "no readings yet"}
              </div>
            </div>

            <div className="space-y-3">
              <PctBar
                label="Session"
                pct={account.current_session_pct}
                resetsAt={account.session_resets_at}
                hint={
                  account.session_window_hours
                    ? `${account.session_window_hours}h window`
                    : undefined
                }
              />
              <PctBar
                label="Weekly · all models"
                pct={account.current_weekly_all_pct}
                resetsAt={account.weekly_resets_at}
              />
              <PctBar
                label="Weekly · Sonnet only"
                pct={account.current_weekly_sonnet_pct}
                resetsAt={account.weekly_resets_at}
                muted
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-medium text-slate-400">
              Update readings (type the percentages claude.ai shows)
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="text-xs text-slate-400">
                Session %
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={draftSessionPct}
                  onChange={(e) => setDraftSessionPct(e.target.value)}
                  placeholder="0–100"
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
                />
              </label>
              <label className="text-xs text-slate-400">
                Weekly · all %
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={draftWeeklyAllPct}
                  onChange={(e) => setDraftWeeklyAllPct(e.target.value)}
                  placeholder="0–100"
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
                />
              </label>
              <label className="text-xs text-slate-400">
                Weekly · Sonnet %
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={draftWeeklySonnetPct}
                  onChange={(e) => setDraftWeeklySonnetPct(e.target.value)}
                  placeholder="0–100"
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
                />
              </label>
              <label className="text-xs text-slate-400">
                Session resets at
                <input
                  type="datetime-local"
                  value={draftSessionResetsAt}
                  onChange={(e) => setDraftSessionResetsAt(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
                />
              </label>
              <label className="text-xs text-slate-400">
                Weekly resets at
                <input
                  type="datetime-local"
                  value={draftWeeklyResetsAt}
                  onChange={(e) => setDraftWeeklyResetsAt(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
                />
              </label>
              <label className="text-xs text-slate-400">
                Session window (hours)
                <input
                  type="number"
                  min={1}
                  max={24}
                  value={draftSessionWindowH}
                  onChange={(e) => setDraftSessionWindowH(e.target.value)}
                  placeholder="default 5"
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
                />
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => submitMaxPlanReading(false)}
                disabled={saving === `max-reading-${account.id}`}
                className="rounded-md bg-violet-600 px-3 py-1 text-xs font-medium text-white hover:bg-violet-500 disabled:opacity-50"
              >
                {saving === `max-reading-${account.id}`
                  ? "Saving…"
                  : "Save readings"}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (
                    confirm(
                      `Mark a fresh session for ${account.label}?\n\n` +
                        `Stamps session_started_at = now AND zeroes the three ` +
                        `percentages (session + weekly all + weekly Sonnet). ` +
                        `Use this when you've just opened a new conversation ` +
                        `after the session timer expired.`,
                    )
                  )
                    submitMaxPlanReading(true);
                }}
                disabled={saving === `max-reading-${account.id}`}
                className="rounded-md border border-amber-700 bg-amber-950/30 px-3 py-1 text-xs text-amber-200 hover:border-amber-400 disabled:opacity-50"
              >
                Session just restarted
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Anthropic's % weighting (model + context + …) is opaque,
              so we don't compute these locally — paste what claude.ai
              shows. Reset timestamps mirror the "resets in" timer on
              that same UI; both weekly bars share one weekly-reset
              moment per Anthropic's "weekly resets seven days after
              session starts" rule.
            </p>
          </div>
        </>
      )}

      {!isCreditMode && !isManualPctMode && (
        <>
          <div className="rounded-md border border-slate-800 bg-slate-950/50 p-3">
            <div className="mb-1 text-xs font-medium text-slate-400">
              Current consumption (since baseline)
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-2xl font-semibold text-white tabular-nums">
                  {fmtNum(account.current_input_tokens_used)}
                </div>
                <div className="text-xs text-slate-400">
                  input · {pctOrDash(
                    account.current_input_tokens_used,
                    account.weekly_input_token_cap,
                  )}{" "}
                  of weekly cap
                </div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-white tabular-nums">
                  {fmtNum(account.current_output_tokens_used)}
                </div>
                <div className="text-xs text-slate-400">
                  output · {pctOrDash(
                    account.current_output_tokens_used,
                    account.weekly_output_token_cap,
                  )}{" "}
                  of weekly cap
                </div>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              baseline {fmtNum(account.baseline_input_tokens_used)} in /{" "}
              {fmtNum(account.baseline_output_tokens_used)} out @{" "}
              {fmtTime(account.baseline_ts)} · delta {fmtNum(account.consumed_since_baseline_input)} /{" "}
              {fmtNum(account.consumed_since_baseline_output)}
            </p>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-medium text-slate-400">
              Paste current usage (from Claude.ai indicator)
            </div>
            <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
              <input
                type="number"
                min={0}
                value={draftBaselineIn}
                onChange={(e) => setDraftBaselineIn(e.target.value)}
                placeholder="input tokens"
                className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
              />
              <input
                type="number"
                min={0}
                value={draftBaselineOut}
                onChange={(e) => setDraftBaselineOut(e.target.value)}
                placeholder="output tokens"
                className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
              />
              <button
                type="button"
                onClick={submitBaseline}
                disabled={isBaselineSaving}
                className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
              >
                {isBaselineSaving ? "Pasting…" : "Set baseline"}
              </button>
            </div>
          </div>
        </>
      )}

      {isCreditMode && (
        <>
          <div className="rounded-md border border-slate-800 bg-slate-950/50 p-3">
            <div className="mb-1 text-xs font-medium text-slate-400">
              Credit balance · USD (since last top-up)
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-2xl font-semibold text-white tabular-nums">
                  {fmtUSD(account.current_balance_cents)}
                </div>
                <div className="text-xs text-slate-400">
                  current balance ·{" "}
                  {account.last_topup_amount_cents !== null &&
                  account.current_balance_cents !== null
                    ? `${Math.round(
                        ((account.last_topup_amount_cents -
                          account.current_balance_cents) /
                          account.last_topup_amount_cents) *
                          100,
                      )}% of top-up consumed`
                    : "(no top-up recorded)"}
                </div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-amber-200 tabular-nums">
                  {account.spent_hcents_since_topup !== null
                    ? fmtUSDFromHcents(account.spent_hcents_since_topup)
                    : "—"}
                </div>
                <div className="text-xs text-slate-400">
                  spent (locked at event-time rates)
                </div>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Last top-up {fmtUSD(account.last_topup_amount_cents)} @{" "}
              {fmtTime(account.last_topup_at)} · auto-recharge at{" "}
              {fmtUSD(account.recharge_threshold_cents)} →{" "}
              {fmtUSD(account.recharge_to_cents)}
            </p>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-medium text-slate-400">
              Record top-up (from Anthropic Console)
            </div>
            <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
              <input
                type="number"
                min={0}
                step="0.01"
                value={draftTopupAmount}
                onChange={(e) => setDraftTopupAmount(e.target.value)}
                placeholder="amount in $ (e.g., 100 or 100.00)"
                className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
              />
              <button
                type="button"
                onClick={submitTopup}
                disabled={isAccountSaving}
                className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
              >
                {isAccountSaving ? "Recording…" : "Record top-up"}
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Stamps <code>last_topup_at</code> to now; cost
              accumulates from event-time rates after this anchor.
              Re-enter when Anthropic auto-recharges your card so
              the balance stays accurate.
            </p>
          </div>
        </>
      )}

      <div className="space-y-2 border-t border-slate-800 pt-3">
        <div className="text-xs font-medium text-slate-400">
          Account config
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="text-xs text-slate-400">
            Label
            <input
              type="text"
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-white focus:border-cyan-400 focus:outline-none"
            />
          </label>
          <label className="text-xs text-slate-400">
            Anthropic account
            <input
              type="text"
              value={draftEmail}
              onChange={(e) => setDraftEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
            />
          </label>
          <label className="text-xs text-slate-400">
            Plan tier
            <select
              value={draftPlan}
              onChange={(e) => setDraftPlan(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-white focus:border-cyan-400 focus:outline-none"
            >
              {planTiers.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <div />
          {!isCreditMode && !isManualPctMode && (
            <>
              <label className="text-xs text-slate-400">
                Weekly input cap
                <input
                  type="number"
                  min={0}
                  value={draftWeeklyIn}
                  onChange={(e) => setDraftWeeklyIn(e.target.value)}
                  placeholder="leave blank if unknown"
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
                />
              </label>
              <label className="text-xs text-slate-400">
                Weekly output cap
                <input
                  type="number"
                  min={0}
                  value={draftWeeklyOut}
                  onChange={(e) => setDraftWeeklyOut(e.target.value)}
                  placeholder="leave blank if unknown"
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
                />
              </label>
            </>
          )}
          {isCreditMode && (
            <>
              <label className="text-xs text-slate-400">
                Re-charge at ($)
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={draftRechargeAt}
                  onChange={(e) => setDraftRechargeAt(e.target.value)}
                  placeholder="threshold (e.g., 10)"
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
                />
              </label>
              <label className="text-xs text-slate-400">
                Re-charge to ($)
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={draftRechargeTo}
                  onChange={(e) => setDraftRechargeTo(e.target.value)}
                  placeholder="auto-fill amount (e.g., 50)"
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-white tabular-nums focus:border-cyan-400 focus:outline-none"
                />
              </label>
            </>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={submitConfig}
            disabled={!dirty || isAccountSaving}
            className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
          >
            {isAccountSaving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={() => onUpdate({ enabled: !account.enabled })}
            disabled={isAccountSaving}
            className="rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-200 hover:border-cyan-400 disabled:opacity-50"
          >
            {account.enabled ? "Disable account" : "Enable account"}
          </button>
        </div>
        <p className="text-xs text-slate-500">
          {isCreditMode ? (
            <>
              Claude API is credit-based. Pick the primary model from
              the plan-tier dropdown — input/output rates are locked
              at event-time so historical events stay accurate even
              when Anthropic changes pricing. The literal API key
              lives at <code>/admin/operations/shadow-orchestrator</code>;
              this row only carries the cost-tracking label.
            </>
          ) : (
            <>
              Anthropic doesn't publish exact Max plan caps — leave fields
              blank if you don't have observed numbers; the dropdown is
              reference framing.
            </>
          )}
        </p>
      </div>
    </AdminCard>
  );
}

// T-082 Phase 5 + Marc 2026-05-09 redo — per-actor utilization.
// Shows token + cost breakdown for each cluster member (frontend-worker,
// backend-worker, wire-test-worker, chat-opus, shadow-orchestrator) so
// it's obvious at a glance who's burning what. Three windows: 24h
// rolling, current week (UTC, anchored Monday 00:00), since baseline.
function ActorUtilization({ actors }: { actors: Actor[] }) {
  if (actors.length === 0) return null;
  // Hide unassigned + zero-event actors entirely if nothing is enabled
  // anywhere — they're noise. If at least one actor has any events,
  // show all rows so it's obvious which workers ARE quiet.
  const anyEvents = actors.some(
    (a) => (a.event_count_total ?? 0) > 0 || (a.last_event_ts ?? null) !== null,
  );
  const totals24h = actors.reduce(
    (acc, a) => ({
      input: acc.input + (a.input_tokens_24h ?? 0),
      output: acc.output + (a.output_tokens_24h ?? 0),
      cost: acc.cost + (a.cost_hcents_24h ?? 0),
    }),
    { input: 0, output: 0, cost: 0 },
  );
  const totalsWeek = actors.reduce(
    (acc, a) => ({
      input: acc.input + (a.input_tokens_week ?? 0),
      output: acc.output + (a.output_tokens_week ?? 0),
      cost: acc.cost + (a.cost_hcents_week ?? 0),
    }),
    { input: 0, output: 0, cost: 0 },
  );

  return (
    <AdminCard
      className="relative overflow-hidden"
    >
      {/* Subtle brand-gradient stripe at the top edge — a small visual
          tie back to the BuddyWatch logo's violet → cyan gradient
          without overpowering the data table. */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-0.5"
        style={{
          background:
            "linear-gradient(90deg, var(--brand-primary) 0%, var(--brand-accent) 100%)",
          opacity: 0.6,
        }}
      />
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-white">
          Per-actor utilization
        </h2>
        <span className="text-sm text-slate-400">
          {anyEvents
            ? `${actors.length} ${actors.length === 1 ? "actor" : "actors"}`
            : "No events recorded yet"}
        </span>
      </div>
      <p className="mb-4 text-sm text-slate-400">
        Token + cost burn per cluster member. Two windows: <em>24h
        rolling</em> ends now; <em>Week</em> anchors on the current
        UTC week's Monday 00:00 (matches the threshold-projection
        evaluator). Cost columns are blank on Max-tier accounts
        (Anthropic doesn't expose per-event dollar values for Max
        plans).
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr
              className="border-b text-left text-xs font-medium text-slate-400"
              style={{ borderColor: "var(--jc-border)" }}
            >
              <th className="py-3 pr-4">Actor</th>
              <th className="py-3 pr-4">Account</th>
              <th className="py-3 pr-4 text-right">24h in</th>
              <th className="py-3 pr-4 text-right">24h out</th>
              <th className="py-3 pr-4 text-right">24h cost</th>
              <th className="py-3 pr-4 text-right">Week in</th>
              <th className="py-3 pr-4 text-right">Week out</th>
              <th className="py-3 pr-4 text-right">Week cost</th>
              <th className="py-3 pr-4">Last event</th>
              <th className="py-3 pr-4 text-right">Events</th>
            </tr>
          </thead>
          <tbody>
            {actors.map((a, idx) => {
              const isQuiet =
                (a.input_tokens_24h ?? 0) === 0 &&
                (a.output_tokens_24h ?? 0) === 0;
              // Liveness dot color: brand-cyan when active in the last
              // hour, brand-violet when active in 24h, dim slate when
              // quiet. Cheap visual signal mapped to brand palette
              // (no random emerald/amber).
              const ageMin = a.last_event_ts
                ? Math.max(0, (Date.now() - new Date(a.last_event_ts).getTime()) / 60000)
                : Infinity;
              const dotColor =
                ageMin < 60
                  ? "var(--brand-accent)"
                  : ageMin < 24 * 60
                    ? "var(--brand-primary)"
                    : "#475569";
              return (
                <tr
                  key={a.actor_handle}
                  className="align-middle transition-colors hover:bg-white/[0.02]"
                  style={{
                    borderBottom: "1px solid var(--jc-border)",
                    background: idx % 2 === 1 ? "rgba(255, 255, 255, 0.015)" : undefined,
                    opacity: isQuiet ? 0.55 : 1,
                  }}
                >
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2.5">
                      <span
                        aria-hidden
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ background: dotColor }}
                        title={
                          a.last_event_ts
                            ? `Last event ${Math.round(ageMin)} min ago`
                            : "No events recorded"
                        }
                      />
                      <span className="text-sm font-medium text-white">
                        {a.actor_handle}
                      </span>
                      {!a.enabled && (
                        <span className="rounded bg-slate-800 px-1.5 py-0.5 text-xs text-slate-400">
                          disabled
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-xs text-slate-400">
                    {a.account_label ?? (
                      <span className="italic text-slate-600">unassigned</span>
                    )}
                    {a.account_plan_tier && (
                      <span className="ml-1 text-xs text-slate-500">
                        ({a.account_plan_tier})
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-right text-sm tabular-nums text-white">
                    {fmtNum(a.input_tokens_24h ?? 0)}
                  </td>
                  <td className="py-3 pr-4 text-right text-sm tabular-nums text-white">
                    {fmtNum(a.output_tokens_24h ?? 0)}
                  </td>
                  <td className="py-3 pr-4 text-right text-sm tabular-nums text-amber-200/90">
                    {(a.cost_hcents_24h ?? 0) > 0
                      ? fmtUSDFromHcents(a.cost_hcents_24h)
                      : "—"}
                  </td>
                  <td className="py-3 pr-4 text-right text-sm tabular-nums text-white">
                    {fmtNum(a.input_tokens_week ?? 0)}
                  </td>
                  <td className="py-3 pr-4 text-right text-sm tabular-nums text-white">
                    {fmtNum(a.output_tokens_week ?? 0)}
                  </td>
                  <td className="py-3 pr-4 text-right text-sm tabular-nums text-amber-200/90">
                    {(a.cost_hcents_week ?? 0) > 0
                      ? fmtUSDFromHcents(a.cost_hcents_week)
                      : "—"}
                  </td>
                  <td
                    className="py-3 pr-4 text-sm text-slate-400"
                    title={a.last_event_ts ?? "no events"}
                  >
                    {a.last_event_ts ? fmtCountdown(a.last_event_ts) : "—"}
                  </td>
                  <td className="py-3 pr-4 text-right text-sm tabular-nums text-slate-400">
                    {fmtNum(a.event_count_total ?? 0)}
                  </td>
                </tr>
              );
            })}
            {/* Cluster totals row */}
            <tr className="border-t-2 border-slate-700 align-top">
              <td className="py-3 pr-4 text-sm font-semibold text-slate-300">
                Cluster total
              </td>
              <td className="py-2 pr-3" />
              <td className="py-3 pr-4 text-right text-sm font-semibold tabular-nums text-white">
                {fmtNum(totals24h.input)}
              </td>
              <td className="py-3 pr-4 text-right text-sm font-semibold tabular-nums text-white">
                {fmtNum(totals24h.output)}
              </td>
              <td className="py-3 pr-4 text-right text-sm font-semibold tabular-nums text-amber-200/90">
                {totals24h.cost > 0 ? fmtUSDFromHcents(totals24h.cost) : "—"}
              </td>
              <td className="py-3 pr-4 text-right text-sm font-semibold tabular-nums text-white">
                {fmtNum(totalsWeek.input)}
              </td>
              <td className="py-3 pr-4 text-right text-sm font-semibold tabular-nums text-white">
                {fmtNum(totalsWeek.output)}
              </td>
              <td className="py-3 pr-4 text-right text-sm font-semibold tabular-nums text-amber-200/90">
                {totalsWeek.cost > 0 ? fmtUSDFromHcents(totalsWeek.cost) : "—"}
              </td>
              <td className="py-2 pr-3" />
              <td className="py-2 pr-3" />
            </tr>
          </tbody>
        </table>
      </div>
    </AdminCard>
  );
}

function ActorAssignments({
  actors,
  accounts,
  saving,
  onAssign,
}: {
  actors: Actor[];
  accounts: Account[];
  saving: string | null;
  onAssign: (
    actor_handle: string,
    account_id: number | null,
    enabled?: boolean,
  ) => void;
}) {
  return (
    <AdminCard>
      <h2 className="mb-3 text-base font-semibold text-white">
        Actor assignment
      </h2>
      <p className="mb-3 text-xs text-slate-400">
        Each agent counts against ONE account. Flip the radio to switch.
        Disabling an actor pauses event roll-up for it.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800 text-left text-xs font-medium text-slate-400">
              <th className="py-2 pr-3">Actor</th>
              <th className="py-2 pr-3">Unassigned</th>
              {accounts.map((a) => (
                <th key={a.id} className="py-2 pr-3">
                  <span className="font-medium text-white">{a.label}</span>
                </th>
              ))}
              <th className="py-2 pr-3">Enabled</th>
            </tr>
          </thead>
          <tbody>
            {actors.map((actor) => {
              const isSaving = saving === `actor-${actor.actor_handle}`;
              return (
                <tr
                  key={actor.actor_handle}
                  className="border-b border-slate-900 align-middle"
                >
                  <td className="py-2 pr-3 text-sm font-medium text-white">
                    {actor.actor_handle}
                  </td>
                  <td className="py-2 pr-3 text-center">
                    <input
                      type="radio"
                      name={`assign-${actor.actor_handle}`}
                      checked={actor.account_id === null}
                      onChange={() => onAssign(actor.actor_handle, null)}
                      disabled={isSaving}
                      className="accent-slate-500"
                    />
                  </td>
                  {accounts.map((a) => (
                    <td key={a.id} className="py-2 pr-3 text-center">
                      <input
                        type="radio"
                        name={`assign-${actor.actor_handle}`}
                        checked={actor.account_id === a.id}
                        onChange={() => onAssign(actor.actor_handle, a.id)}
                        disabled={isSaving}
                        className="accent-emerald-500"
                      />
                    </td>
                  ))}
                  <td className="py-2 pr-3 text-center">
                    <input
                      type="checkbox"
                      checked={actor.enabled}
                      onChange={(e) =>
                        onAssign(
                          actor.actor_handle,
                          actor.account_id,
                          e.target.checked,
                        )
                      }
                      disabled={isSaving}
                      className="accent-emerald-500"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AdminCard>
  );
}

function ThresholdTable({ thresholds }: { thresholds: Threshold[] }) {
  return (
    <AdminCard>
      <h2 className="mb-2 text-base font-semibold text-white">Thresholds</h2>
      <p className="mb-3 text-xs text-slate-400">
        Account-scoped alert ladder. NULL account = wildcard ("all"). Editing
        deferred to the next wave; current alerts use these directly once
        Phase 3 projection job lands.
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-800 text-left text-xs font-medium text-slate-400">
            <th className="py-2 pr-3">Account</th>
            <th className="py-2 pr-3">Period</th>
            <th className="py-2 pr-3">Alert @</th>
            <th className="py-2 pr-3">Project alert @</th>
            <th className="py-2 pr-3">Cooldown</th>
          </tr>
        </thead>
        <tbody>
          {thresholds.map((t) => (
            <tr key={t.id} className="border-b border-slate-900">
              <td className="py-1.5 pr-3 text-sm text-slate-300">
                {t.account_label ?? "all"}
              </td>
              <td className="py-1.5 pr-3 text-sm text-slate-400">
                {t.period_type}
              </td>
              <td className="py-1.5 pr-3 text-sm tabular-nums">
                {t.alert_at_pct === null ? "—" : `${t.alert_at_pct}%`}
              </td>
              <td className="py-1.5 pr-3 text-sm tabular-nums">
                {t.project_alert_at_pct === null
                  ? "—"
                  : `${t.project_alert_at_pct}%`}
              </td>
              <td className="py-1.5 pr-3 text-sm text-slate-400">
                {t.cooldown_hours}h
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </AdminCard>
  );
}

function AlertsTable({ alerts }: { alerts: Alert[] }) {
  return (
    <AdminCard>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-white">
          Recent threshold alerts
        </h2>
        <span className="text-xs text-slate-400">
          {alerts.length === 0 ? "none" : `${alerts.length} most-recent`}
        </span>
      </div>
      <p className="mb-3 text-xs text-slate-400">
        Each row is one threshold-crossing event. "Run projection"
        evaluates every enabled account against its threshold ladder
        (and cron fires the same pass every 30 minutes once wired).
        Recommendation text is hardcoded per severity band v1; the
        queue-aware rules engine ships in a follow-up.
      </p>
      {alerts.length === 0 && (
        <p className="text-xs text-slate-500">
          No alerts yet — either no account has crossed a threshold,
          or projection hasn't been run since you set baselines /
          caps. Click "Run projection" above.
        </p>
      )}
      {alerts.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs font-medium text-slate-400">
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">Account</th>
                <th className="py-2 pr-3">Period</th>
                <th className="py-2 pr-3">Current %</th>
                <th className="py-2 pr-3">Projected %</th>
                <th className="py-2 pr-3">Recommendation</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => {
                const severeProj =
                  a.projected_pct_at_period_end !== null &&
                  a.projected_pct_at_period_end >= 125;
                const severeCur =
                  a.threshold_pct_at_alert !== null &&
                  a.threshold_pct_at_alert >= 90;
                const tone = severeProj || severeCur
                  ? "text-rose-300"
                  : "text-amber-300";
                return (
                  <tr
                    key={a.id}
                    className="border-b border-slate-900 align-top"
                  >
                    <td className="py-3 pr-4 text-sm text-slate-400">
                      {fmtTime(a.ts)}
                    </td>
                    <td className="py-2 pr-3 text-sm font-medium text-white">
                      {a.account_label ?? `#${a.account_id}`}
                    </td>
                    <td className="py-2 pr-3 text-sm text-slate-400">
                      {a.period_type}
                    </td>
                    <td className={`py-2 pr-3 text-sm tabular-nums ${tone}`}>
                      {a.threshold_pct_at_alert === null
                        ? "—"
                        : `${a.threshold_pct_at_alert}%`}
                    </td>
                    <td className={`py-2 pr-3 text-sm tabular-nums ${tone}`}>
                      {a.projected_pct_at_period_end === null
                        ? "—"
                        : `${a.projected_pct_at_period_end}%`}
                    </td>
                    <td className="py-2 pr-3 text-xs text-slate-300">
                      {a.recommendation_text || (
                        <span className="italic text-slate-500">
                          (no recommendation — band &lt; 110%)
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminCard>
  );
}

function PctBar({
  label,
  pct,
  resetsAt,
  hint,
  muted = false,
}: {
  label: string;
  pct: number | null | undefined;
  resetsAt: string | null | undefined;
  hint?: string;
  muted?: boolean;
}) {
  const safePct = pct === null || pct === undefined ? 0 : Math.max(0, Math.min(100, pct));
  const display = pct === null || pct === undefined ? "—" : `${pct}%`;
  return (
    <div className={muted ? "opacity-80" : ""}>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-slate-200">
          {label}
          {hint ? <span className="ml-1 text-xs text-slate-400">· {hint}</span> : null}
        </span>
        <span className="text-sm font-medium tabular-nums text-white">{display}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
        <div
          className={`h-full ${pctBarColor(pct)} transition-[width]`}
          style={{ width: `${safePct}%` }}
        />
      </div>
      <div className="mt-1 text-xs text-slate-400">
        resets {fmtCountdown(resetsAt)}
        {resetsAt ? <span className="ml-1 text-slate-600">· {fmtTime(resetsAt)}</span> : null}
      </div>
    </div>
  );
}
